import { type ProcedureInitiative } from "@overmap-ai/common"
import { selectProcedureInitiativeById, selectProcedureInitiativesByIds } from "@overmap-ai/core"

import { appStore } from "../../store"
import { FilterModifier } from "./FilterModifier"

const deserializeProcedureInitiativeId = (filterValue: ProcedureInitiative["uuid"] | null) => {
	if (!filterValue) return null

	const procedureInitiative = selectProcedureInitiativeById(filterValue)(appStore.getState())

	return procedureInitiative?.uuid
}

export const ProcedureInitiativeEqualsFilterModifier = FilterModifier.create<
	ProcedureInitiative["uuid"] | null,
	ProcedureInitiative["uuid"] | null
>({
	id: "procedure-initiative-equals",
	getApplyFn: (filterValue) => (value) => {
		return value === filterValue
	},
	render: () => "is",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeProcedureInitiativeId,
})

export const ProcedureInitiativeNotEqualsFilterModifier = FilterModifier.create<
	ProcedureInitiative["uuid"] | null,
	ProcedureInitiative["uuid"] | null
>({
	id: "procedure-initiative-not-equals",
	getApplyFn: (filterValue) => (value) => {
		return value !== filterValue
	},
	render: () => "is not",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeProcedureInitiativeId,
})

const deserializeProcedureInitiativeIds = (filterValue: (ProcedureInitiative["uuid"] | null)[]) => {
	const procedureInitiativeIds = filterValue.filter(Boolean) as string[]
	const procedureInitiatives = selectProcedureInitiativesByIds(procedureInitiativeIds)(appStore.getState())
	const foundProcedureInitiativeIds: (string | null)[] = procedureInitiatives.map(
		(procedureInitiative) => procedureInitiative.uuid,
	)

	if (new Set(filterValue).has(null)) {
		foundProcedureInitiativeIds.push(null)
	}

	return foundProcedureInitiativeIds.length > 0 ? foundProcedureInitiativeIds : undefined
}

export const ProcedureInitiativeIncludesFilterModifier = FilterModifier.create<
	ProcedureInitiative["uuid"] | null,
	(ProcedureInitiative["uuid"] | null)[]
>({
	id: "procedure-initiative-includes",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			return filterValueSet.has(value)
		}
	},
	render: () => "is any of",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeProcedureInitiativeIds,
})

export const ProcedureInitiativeExcludesFilterModifier = FilterModifier.create<
	ProcedureInitiative["uuid"] | null,
	(ProcedureInitiative["uuid"] | null)[]
>({
	id: "procedure-initiative-excludes",
	getApplyFn: (filterValue) => {
		const filterValueSet = new Set(filterValue)
		return (value) => {
			return !filterValueSet.has(value)
		}
	},
	render: () => "is none of",
	serialize: (filterValue) => filterValue,
	deserialize: deserializeProcedureInitiativeIds,
})
